import React, { useEffect, useRef, useState } from 'react';
import debounce from 'debounce';
import { isEmpty } from 'ramda';
import { useTranslation } from 'react-i18next';
import { userStore } from '../../store';
import api from '../../api';
import { isNotEmpty } from '../../js/fpUtils';
import { parseDealershipTitle } from '../../js/helpers';
import { SEARCH_DEBOUNCE_VALUE } from '../../js/_constants';
import SearchableDealerList from '../searchableDealerList/SearchableDealerList';
import { RESPONSE } from '../../js/_constants';
import './DealershipList.scss';

const DealershipList = ({ onClick }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [dealers, setDealers] = useState<any>([]);
    const { t } = useTranslation();
    const { details, dealershipGroups, dealerships  } = userStore();
    const initialOptions = useRef<any>([]);

    const fetchAndSetOptions = async (query?: string) => {
        /**
         * WHen user deletes a query, we can immediately fallback to the initial options.
         * Initial options are either defaultOptions passed in as props,
         * or search results of the first search when component mounts
         */
        if (isNotEmpty(initialOptions.current) && query === '') {
            setDealers(initialOptions.current);
            return;
        }

        setIsLoading(true);

        const params = {
            brand: details.userGrantedBrands,
            ...(query && {
                title: query,
                kvps: query
            })
        };

        const dealershipsCollection = await api.user.getUserDealerships({
            params
        });

        const dealershipOptions = dealershipsCollection[
            RESPONSE.EXPAND_COLLECTION
        ].map(dealership => ({title: parseDealershipTitle(dealership), kvps: dealership.kvps}));

        if (initialOptions.current && isEmpty(initialOptions.current)) {
            initialOptions.current = dealershipOptions;
        }

        setDealers(dealershipOptions);
        setIsLoading(false);
    };

    useEffect(() => {
        fetchAndSetOptions();
    }, []);

    const handleQueryChange = async (query: string) => {
        fetchAndSetOptions(query);
    };

    const debouncedQueryChange = debounce(
        handleQueryChange,
        SEARCH_DEBOUNCE_VALUE
    );

    if (dealershipGroups.length === 0 && dealerships.totalItems === 0) {
        return <p className="field no-dealers-and-groups" data-testid="no-dealers-and-groups">{t('no_dealership_assigned')}</p>
    }

    if (dealerships.totalItems === 0) {
        return <p data-testid="no-dealership-assigned">{t('no_dealership_assigned')}</p>
    }

    return (
        <SearchableDealerList
            isLoading={isLoading}
            label={t('dealership')}
            onQueryChange={debouncedQueryChange}
            options={dealers}
            totalDealers={dealerships.totalItems}
            searchPlaceholder={t('search')}
            onClick={onClick}
        />
    );
};

export default DealershipList;
